import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Engine } from '@rendley/sdk';

import type { Content } from '@/entities/content/api/types';
import { toastError } from '@/shared/lib/toastManager';
import Player from '@/widgets/player';

type Props = {
  renderedVideoURL?: string;
  rendleyEngine?: Engine;
  rendleyCanvas?: HTMLCanvasElement;
};

export function VideoPlayerWrapper({
  renderedVideoURL,
  rendleyEngine,
  rendleyCanvas,
}: Props) {
  const { control } = useFormContext<Content>();
  const content = useWatch({ control }) as Partial<Content>;
  const resolution = rendleyEngine?.getDisplay().getResolution();

  const bridge = useMemo(() => {
    if (rendleyEngine && rendleyCanvas && resolution && resolution[0] <= 1280) {
      return {
        mediaStream: rendleyCanvas.captureStream(25),
        getIsPlaying: () => {
          return rendleyEngine.getTimeline().isPlaying;
        },
        getDuration: () => {
          return rendleyEngine.getTimeline().getFitDuration();
        },
        getCurrentTime: () => {
          return rendleyEngine.getTimeline().currentTime;
        },
        togglePlay: () => {
          if (rendleyEngine.getTimeline().isPlaying) {
            rendleyEngine.pause();
          } else {
            if (
              rendleyEngine.getTimeline().getFitDuration() -
                rendleyEngine.getTimeline().currentTime <
              0.5
            ) {
              rendleyEngine.seek(0);
            }

            rendleyEngine.play();
          }
        },
        seek: (time: number) => {
          rendleyEngine.seek(time);
        },
        toggleMute: () => {
          toastError('Not available in preview mode');
        },
        toggleFullscreen: () => {
          toastError('Not available in preview mode');
        },
      };
    }

    return undefined;
  }, [rendleyEngine, rendleyCanvas, resolution]);

  return (
    <Player
      type="video"
      content={content}
      renderedVideoURL={renderedVideoURL}
      bridge={bridge}
      resetHLS
    />
  );
}
